import { useTranslate } from "@similarweb/i18n-provider";
import { Crown } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tab, TabList, Tabs } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useCompanyName } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCompanyName";
import type {
    AppsDigitalAssetRecord,
    WebsitesDigitalAssetRecord,
} from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";
import { digitalAssetTypeMap } from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { useDigitalAssetsTabSelection } from "../../../digital-assets/hooks/useDigitalAssetsTabSelection";
import { StyledTabBarWrapper, StyledUpgradeIcon } from "../styles";

import { AppsTabContent } from "./AppsTabContent";
import WebsitesTabContent from "./WebsitesTabContent";

type Props = {
    appsTabData: {
        data: readonly AppsDigitalAssetRecord[] | undefined;
        isFetched: boolean;
        isLoading: boolean;
    };
    websitesTabData: {
        data: readonly WebsitesDigitalAssetRecord[] | undefined;
        isFetched: boolean;
        isLoading: boolean;
    };
};

const CardContent = ({ appsTabData, websitesTabData }: Props) => {
    const { translate } = useTranslate();

    const companyName = useCompanyName();

    const isNoAppsDataPresent = appsTabData.isFetched && !appsTabData.data?.length;

    const { activeTab, selectedTabIndex, onTabSelect } = useDigitalAssetsTabSelection();

    if (isNoAppsDataPresent) {
        return (
            <WebsitesTabContent
                companyName={companyName}
                data={websitesTabData.data}
                isLoading={websitesTabData.isLoading}
                isFetched={websitesTabData.isFetched}
            />
        );
    }

    return (
        <>
            <StyledTabBarWrapper>
                <Tabs
                    selectedIndex={selectedTabIndex}
                    onSelect={onTabSelect}
                >
                    <TabList>
                        <Tab data-automation={`digital-assets-tab-${digitalAssetTypeMap.websites}`}>
                            {translate("si.company-analysis.digital-assets.tabs.websites")}
                        </Tab>

                        <Tab data-automation={`digital-assets-tab-${digitalAssetTypeMap.apps}`}>
                            <Flex gap="12px">
                                {translate("si.company-analysis.digital-assets.tabs.apps")}

                                {salesSettingsHelper.isConsolidatedAppsEnabled ? null : (
                                    <StyledUpgradeIcon>
                                        <Crown
                                            size="xs"
                                            color={colorsPalettes.indigo[300]}
                                        />
                                    </StyledUpgradeIcon>
                                )}
                            </Flex>
                        </Tab>
                    </TabList>
                </Tabs>
            </StyledTabBarWrapper>

            {activeTab === digitalAssetTypeMap.websites ? (
                <WebsitesTabContent
                    companyName={companyName}
                    data={websitesTabData.data}
                    isLoading={websitesTabData.isLoading}
                    isFetched={websitesTabData.isFetched}
                />
            ) : (
                <AppsTabContent
                    data={appsTabData.data}
                    isLoading={appsTabData.isLoading}
                />
            )}
        </>
    );
};

export default CardContent;
